/**
 * Shared DTOs and Zod schemas for the Travel route.
 *
 * A trip is a container: dates + notes + a packing checklist, with
 * tasks (obligations) and documents linking in via their `trip_id`.
 * The packing list travels as one JSON array on the trip row — edits
 * are whole-list, single-user, low-frequency, exactly like budget
 * month snapshots.
 */

import { z } from 'zod';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const packingItemSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(160),
  /** Optional quantity/detail note — '2 bags', 'x4'. */
  qty: z.string().max(40).nullish(),
  done: z.boolean().default(false),
});
export type PackingItem = z.infer<typeof packingItemSchema>;

export const packingListSchema = z.array(packingItemSchema).max(200);

export const tripVisibilities = ['private', 'shared'] as const;
export type TripVisibility = (typeof tripVisibilities)[number];

const baseTripFields = {
  title: z.string().min(1).max(160),
  destination: z.string().max(160).nullish(),
  startDate: isoDate,
  endDate: isoDate.nullish(),
  notes: z.string().max(4000).nullish(),
  packing: packingListSchema.default([]),
  visibility: z.enum(tripVisibilities).default('private'),
} as const;

export const tripCreateSchema = z.object(baseTripFields);
export const tripUpdateSchema = z.object(baseTripFields).partial().extend({
  // .partial() keeps .default()s alive — an omitted field would parse
  // back to its default and clobber the stored value (same trap as
  // obligationUpdateSchema). Plain optionals mean "leave unchanged".
  packing: packingListSchema.optional(),
  visibility: z.enum(tripVisibilities).optional(),
});

export type TripCreateInput = z.infer<typeof tripCreateSchema>;
export type TripUpdateInput = z.infer<typeof tripUpdateSchema>;

export interface TripDTO {
  id: string;
  title: string;
  destination: string | null;
  startDate: string;
  endDate: string | null;
  notes: string | null;
  packing: PackingItem[];
  visibility: TripVisibility;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Input for the packing-suggestion endpoint (no trip row needed —
 *  suggestions work before the trip is saved). */
export const packingSuggestSchema = z.object({
  title: z.string().min(1).max(160),
  destination: z.string().max(160).nullish(),
  startDate: isoDate,
  endDate: isoDate.nullish(),
  notes: z.string().max(4000).nullish(),
});
export type PackingSuggestInput = z.infer<typeof packingSuggestSchema>;
